import { useEffect, useState } from 'react';
import { supabase } from '@/lib/supabase';
import { useAuth } from '@/contexts/AuthContext';
import { useMyOrgs, useActiveOrgId } from './orgs';

type EmployeeState =
  | { status: 'loading' }
  | { status: 'ready'; orgId: string }
  | { status: 'no_invite' }
  | { status: 'error'; message: string };

/**
 * Resolves the org the user is currently working in on the employee surface.
 *
 * Membership comes from `my_pulse_orgs()` (every org, employee + admin rows).
 * The active org is persisted in localStorage so a user in several orgs can
 * switch. If the active org has no `pulse_employees` row yet, we call
 * bootstrap-employee for THAT org, which consumes a pending invite for it and
 * lets the pulse_apply_pending_on_signin trigger attach the invited bands.
 */
export function useEmployee(): EmployeeState {
  const { user, isLoading: authLoading } = useAuth();
  const { data: orgs, isLoading: orgsLoading, error: orgsError, refetch } = useMyOrgs();
  const activeOrgId = useActiveOrgId(orgs);
  const [state, setState] = useState<EmployeeState>({ status: 'loading' });

  useEffect(() => {
    if (authLoading || orgsLoading) {
      setState({ status: 'loading' });
      return;
    }
    if (!user) {
      setState({ status: 'loading' });
      return;
    }
    if (orgsError) {
      setState({ status: 'error', message: (orgsError as Error).message });
      return;
    }

    let cancelled = false;

    (async () => {
      const active = activeOrgId ? orgs?.find((o) => o.org_id === activeOrgId) : undefined;

      // Already provisioned in the active org — nothing to do.
      if (active?.has_employee) {
        setState({ status: 'ready', orgId: active.org_id });
        return;
      }

      // Either no org at all (fresh invite) or an org where we're a member but
      // not yet an employee. Both go through bootstrap-employee.
      const { data, error } = await supabase.functions.invoke('bootstrap-employee', {
        body: active ? { org_id: active.org_id } : {},
      });

      if (cancelled) return;

      // FunctionsHttpError leaves data=null and stuffs the Response on
      // error.context. Parse it so we can distinguish no_invite from
      // generic failures and surface a real message.
      let parsed: any = data;
      if (error && (error as any).context) {
        try {
          parsed = await (error as any).context.clone().json();
        } catch {
          try {
            parsed = { error: await (error as any).context.clone().text() };
          } catch {
            /* keep parsed = data */
          }
        }
      }

      if (error || parsed?.error) {
        if (parsed?.error === 'no_invite') {
          // Member of the org but no invitation row: still a valid org.
          if (active) setState({ status: 'ready', orgId: active.org_id });
          else setState({ status: 'no_invite' });
        } else {
          // eslint-disable-next-line no-console
          console.error('[useEmployee] bootstrap-employee failed:', { error, parsed });
          if (active) {
            setState({ status: 'ready', orgId: active.org_id });
          } else {
            const msg =
              parsed?.detail ??
              parsed?.message ??
              parsed?.error ??
              (error as any)?.message ??
              'bootstrap_failed';
            setState({ status: 'error', message: String(msg) });
          }
        }
        return;
      }

      const bootstrappedOrgId = (parsed as any)?.employee?.org_id;
      if (!bootstrappedOrgId) {
        setState({ status: 'error', message: 'bootstrap returned no org_id' });
        return;
      }
      // Refresh membership so has_employee flips and the switcher shows it.
      refetch();
      setState({ status: 'ready', orgId: bootstrappedOrgId });
    })();

    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user, authLoading, orgsLoading, orgsError, activeOrgId, orgs]);

  return state;
}

/** Convenience: returns orgId or null. */
export function useOrgId(): string | null {
  const s = useEmployee();
  return s.status === 'ready' ? s.orgId : null;
}
